import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../controllers/authController";
import { trackActivity, resetSessionId } from "../utils/activityTracker";
import "../styles/Login.css";
import loginVideo from "../assets/vedio.mp4";
import logoImage from "../assets/logo-dark.png";

const Login = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Prevent scrolling on mount
        document.body.style.overflow = "hidden";
        return () => {
            // Re-enable scrolling on unmount
            document.body.style.overflow = "auto";
        };
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const user = await loginUser(username, password);
            console.log("Login Success:", user);
            // store username for LoadDistribution API
            localStorage.setItem("username", username);

            // Start new tracking session
            resetSessionId();
            await trackActivity("LOGIN");

            navigate("/"); // go to Dashboard
        } catch (err) {
            setError("Invalid username or password");
        } finally {
            setLoading(false);
        }
    };

    const togglePassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="login-container">
            {/* Right Side - Video Animation (Now on Left) */}
            <div className="right-section">
                <div className="logo-top-video">
                    <img src={logoImage} alt="EnViz Logo" className="video-logo" />
                </div>
                <video autoPlay muted loop playsInline className="bg-video">
                    <source src={loginVideo} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="video-overlay"></div>

                <div className="right-content">
                    <h2 className="tagline">unlock energy <span>efficiency</span></h2>
                    {/* <p className="description">empower your business with advanced electricity analytics, reduce costs and
                        enhance sustainability.</p> */}
                </div>
            </div>

            {/* Left Side - Login Form (Now on Right) */}
            <div className="left-section">
                {/* Background Gradient */}
                <div className="gradient-bg"></div>

                {/* Login Card */}
                <div className="login-card">
                    {/* Logo inside card */}
                    <div className="card-logo">
                        <img
                            src="/images/enviz-logo.png"
                            alt="EnViz Logo"
                            className="logo"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = '<h2 style="font-size: 36px; margin: 0;"><span style="color: #1BA08E;">en</span><span style="color: #3D5DAA;">Viz</span></h2>';
                            }}
                        />
                    </div>

                    <h1 className="welcome-title">welcome back</h1>
                    <p className="welcome-subtitle">Sign in to unlock smarter energy insights and efficiency.</p>

                    <form className="login-form" onSubmit={handleLogin} id="loginForm">
                        <div className="form-group">
                            <label htmlFor="username">username</label>
                            <div className="input-wrapper">
                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    placeholder="Enter your username"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                />
                                <span className="input-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <circle cx="12" cy="8" r="4" />
                                        <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
                                    </svg>
                                </span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">password</label>
                            <div className="input-wrapper">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    name="password"
                                    placeholder="••••••••••"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <span className="input-icon toggle-password" onClick={togglePassword} style={{ cursor: 'pointer' }}>
                                    {!showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="eye-icon">
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="eye-off-icon">
                                            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                                            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                                            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                                            <line x1="2" x2="22" y1="2" y2="22" />
                                        </svg>
                                    )}
                                </span>
                            </div>
                        </div>

                        {error && <div style={{ color: '#e74c3c', fontSize: '13px', textAlign: 'center', backgroundColor: '#fdecea', padding: '8px', borderRadius: '4px' }}>{error}</div>}

                        <div className="form-options">
                            <label className="remember-me">
                                <input type="checkbox" name="remember" />
                                <span className="checkmark"></span>
                                remember me
                            </label>
                            <a href="#" className="forgot-password">forgot password?</a>
                        </div>

                        <button type="submit" className="btn-signin" disabled={loading}>
                            {loading ? "signing in..." : "sign in"}
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14" />
                                <path d="m12 5 7 7-7 7" />
                            </svg>
                        </button>

                        {/* <div className="divider">
                            <span>or continue with</span>
                        </div> */}

                        {/* <div className="social-buttons">
                            <button type="button" className="btn-social">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                    <path fill="#4285F4"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                google
                            </button>
                            <button type="button" className="btn-social">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                    <path fill="#00A4EF" d="M11.4 24H0V12.6h11.4V24z" />
                                    <path fill="#FFB900" d="M24 24H12.6V12.6H24V24z" />
                                    <path fill="#F25022" d="M11.4 11.4H0V0h11.4v11.4z" />
                                    <path fill="#7FBA00" d="M24 11.4H12.6V0H24v11.4z" />
                                </svg>
                                microsoft
                            </button>
                        </div> */}

                        {/* <p className="signup-link">
                            don't have an account? <a href="#">request demo</a>
                        </p> */}
                    </form>

                    <p className="copyright">© 2026 enfo.ai all rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default Login;